import { createContext, useContext, useEffect, useMemo, useRef } from "react"
import type { RunDryForecast, ForecastUnavailable } from "@hydrologiq/gql-experience/graphql"
import { useCombinedForecast } from "@hydrologiq/shared/hooks/forecast/useCombinedForecast"
import { isSectionVisible } from "@hydrologiq/shared/hooks/sectionVisibility"

interface ForecastContextValue {
  hourlyForecast?: RunDryForecast
  dailyForecast?: RunDryForecast
  hourlyForecastUnavailable?: ForecastUnavailable
  dailyForecastUnavailable?: ForecastUnavailable
  hourlyLoading: boolean
  dailyLoading: boolean
  refreshing: boolean
  refetchHourly: () => void
  refetchDaily: () => void
}

const ForecastContext = createContext<ForecastContextValue | null>(null)

interface AssetForecastProviderProps {
  entityType: "asset"
  assetId: string
  children: React.ReactNode
}

interface InstallationForecastProviderProps {
  entityType: "installation"
  installationId: string
  siteId?: string
  customerId?: string
  children: React.ReactNode
}

type ForecastProviderProps = AssetForecastProviderProps | InstallationForecastProviderProps

export function ForecastProvider(props: ForecastProviderProps) {
  const forecastParams = props.entityType === "asset"
    ? { entityType: "asset" as const, assetId: (props as AssetForecastProviderProps).assetId }
    : {
        entityType: "installation" as const,
        installationId: (props as InstallationForecastProviderProps).installationId,
        siteId: (props as InstallationForecastProviderProps).siteId,
        customerId: (props as InstallationForecastProviderProps).customerId,
      }

  const {
    dailyForecast,
    hourlyForecast,
    dailyForecastUnavailable,
    hourlyForecastUnavailable,
    loading,
    refreshing,
    refetch,
  } = useCombinedForecast({
    ...forecastParams,
    days: 30,
    hours: 24 * 7,
  })

  // Refetch when the section becomes visible again (handles hub section switching).
  // The hub hides inactive sections with visibility:hidden on a light DOM ancestor
  // outside the shadow DOM. We check the shadow host's ancestor visibility.
  const sentinelRef = useRef<HTMLSpanElement>(null)
  const wasHiddenRef = useRef(false)

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return

    const checkVisibility = () => {
      const isVisible = isSectionVisible(el)

      if (isVisible && wasHiddenRef.current) {
        wasHiddenRef.current = false
        refetch()
      } else if (!isVisible) {
        wasHiddenRef.current = true
      }
    }

    // Check on an interval — lightweight style reads
    const interval = setInterval(checkVisibility, 1000)
    // Also refetch on browser tab becoming visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVisibility()
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [refetch])

  const value = useMemo<ForecastContextValue>(() => ({
    hourlyForecast,
    dailyForecast,
    hourlyForecastUnavailable,
    dailyForecastUnavailable,
    hourlyLoading: loading,
    dailyLoading: loading,
    refreshing,
    refetchHourly: refetch,
    refetchDaily: refetch,
  }), [
    hourlyForecast,
    dailyForecast,
    hourlyForecastUnavailable,
    dailyForecastUnavailable,
    loading,
    refreshing,
    refetch,
  ])

  return (
    <ForecastContext.Provider value={value}>
      <span ref={sentinelRef} style={{ position: "absolute", width: "1px", height: "1px", opacity: 0, pointerEvents: "none" }} />
      {props.children}
    </ForecastContext.Provider>
  )
}

export function useForecastContext(): ForecastContextValue {
  const context = useContext(ForecastContext)
  if (!context) {
    throw new Error("useForecastContext must be used within a ForecastProvider")
  }
  return context
}
